import { cn } from '../../lib/utils';
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '../../utils/types';
import { VariantProps, cva } from 'class-variance-authority';
import React, { forwardRef } from 'react';

const textStyles = cva('RXC_TEXT tw-w-full', {
  variants: {
    emphasis: {
      low: 'tw-text-gray-600 tw-font-light',
    },
    size: {
      esm: 'tw-text-esm',
      xs: 'tw-text-xs',
      sm: 'tw-text-sm',
      base: 'tw-text-base',
      lg: 'tw-text-lg',
      xl: 'tw-text-xl',
      '2xl': 'tw-text-2xl',
      modalTitle: 'tw-text-modalTitle',
      '3xl': 'tw-text-3xl',
    },
    weight: {
      thin: 'tw-font-thin',
      normal: 'tw-font-normal',
      medium: 'tw-font-medium',
      semibold: 'tw-font-semibold',
      bold: 'tw-font-bold',
      black: 'tw-font-black',
      extrabold: 'tw-font-900',
    },
    align: {
      left: 'tw-text-left',
      center: 'tw-text-center',
      right: 'tw-text-right',
    },
    italic: {
      true: 'tw-italic',
    },
    underline: {
      true: 'tw-underline tw-underline-offset-2',
    },
    uppercase: {
      true: 'tw-uppercase',
    },
    font: {
      primary: 'tw-font-primary',
      secondary: 'tw-font-secondary',
    },
    decoration: {
      through: 'tw-line-through',
    },
  },
  defaultVariants: {
    size: 'base',
    align: 'left',
    font: 'primary',
  },
});

type TextProps<C extends React.ElementType> = PolymorphicComponentPropsWithRef<
  C,
  VariantProps<typeof textStyles>
>;

type TextComponent = <C extends React.ElementType = 'span'>(
  props: TextProps<C>
) => React.ReactElement | null;

// @ts-expect-error - unexpected typing errors
const Text: TextComponent = forwardRef(
  <C extends React.ElementType = 'span'>(
    {
      as,
      font,
      align,
      size,
      emphasis,
      italic,
      underline,
      uppercase,
      decoration,
      weight,
      className,
      ...props
    }: TextProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || 'span';

    return (
      <Component
        ref={ref}
        className={cn(
          textStyles({
            font,
            size,
            weight,
            decoration,
            emphasis,
            uppercase,
            italic,
            underline,
            align,
            className,
          })
        )}
        {...props}
      />
    );
  }
);

export default Text;
